// @ts-check
// SCIL-17770: contract for applying SciSearch AI filters to an organisation
// profile's shared People/Activity filter slice (`state.organisation.filters[orgId]`).
//
// The org slice feeds `buildQuery(filters, null, null, true)` — the SAME flat
// keys the main-search `search` slice uses, with two deliberate renames:
//   - AI `category`              -> org slice `workItemCategories`
//   - AI `organisationGeography` -> org slice `country`  (org Location keys off Country)
// and one translation:
//   - AI `recentlyMovedOrg: true` -> org slice `timeAtCompany` '0 - 6 months'
//     (SCIL-19355: same OrganisationStartDate range, and the org profile has
//     no Moved organisation control, so it shows as the Time at Company band)
//
// Anything the org profile does not support (organisation-scope search, VC /
// acquisition metrics, super-org roll-ups, list scoping, sortBy, …) is dropped
// and reported so Phase 2's apply call site can surface what was ignored.

import { commonDateRanges } from 'services/inputTokens';

/**
 * Keys accepted verbatim onto the org slice. These are exactly the keys the
 * org People/Activity panels expose and that `buildQuery` understands for the
 * organisation-page search shape.
 * @type {ReadonlySet<string>}
 */
export const allowedAiFilterKeys = new Set([
  // Search term (the org People/Activity term bars read/write `filters[orgId].term`)
  'term',
  // Location
  'country',
  'state',
  'city',
  'postcode',
  // Contacts
  'jobTitle',
  'fullName',
  'lastName',
  'timeAtCompany',
  // Date range dropdown
  'date',
  // Funding
  'dateAwarded',
  'minFunding',
  'maxFunding',
  'grantExpiry',
  'earlyAwardDate',
  'nihApplicationType',
  'nihActivityCode',
  'nihInstituteCode',
  'core',
  // Tradeshows
  'tradeshow',
  'thisShowOnly',
  'sessionType',
  'thisSessionTypeOnly',
  'roleType',
  'thisRoleTypeOnly',
  'exhibitingShow',
  'presentingAtTradeshow',
  'tradeshowCountryRegion',
  'tradeshowState',
  'tradeshowCity',
  // Clinical Trials (main-search sponsor-NAME semantics)
  'phase',
  'phaseStartDate',
  'overallStatus',
  'startDate',
  'roles',
  'hasClinicalTrials',
  'leadSponsor',
  'sponsors',
  // Advanced / KOL
  'kol',
  'totalPublications',
  'totalGrants',
  'threeYearHIndexAverage',
  'totalCollaboratorsOverall',
  'totalFirstNamedAuthorCount',
  'totalLastNamedAuthorCount',
  'journal',
  'firstAuthor',
  'middleAuthor',
  'lastAuthor',
  'validEmails',
  'excludeRecentlyExportedEmails',
  // Contact lists
  'lists',
  'excludeLists',
]);

/**
 * AI key -> org-slice key renames. Applied after the allow check, so callers
 * express these under their AI names and land on the org shape.
 * @type {Readonly<Record<string, string>>}
 */
export const aiKeyTranslations = Object.freeze({
  category: 'workItemCategories',
  organisationGeography: 'country',
});

/**
 * The nested `namedAuthor` group is expressed by AI as flat sibling booleans
 * (`firstAuthor` / `middleAuthor` / `lastAuthor`). buildQuery reads them from a
 * nested `namedAuthor` object, so we fold them together here.
 * @type {ReadonlyArray<string>}
 */
const namedAuthorMemberKeys = ['firstAuthor', 'middleAuthor', 'lastAuthor'];

// Main search's Moved organisation checkbox and this Time at Company band are
// the same OrganisationStartDate:[now-6M TO now] range. Must match the option
// string exactly: timeAtCompanyFormat parses it by position.
const recentlyMovedOrgKey = 'recentlyMovedOrg';
const recentlyMovedBand = '0 - 6 months';

/**
 * Add the recently-moved band to a Time at Company value without duplicating
 * it. An exclusion of the same band wins: those people were explicitly ruled
 * out, so an included copy would only contradict it.
 *
 * @param {any} bands
 * @returns {string[]}
 */
const withRecentlyMovedBand = bands => {
  const current = Array.isArray(bands) ? bands : [];
  if (
    current.includes(recentlyMovedBand) ||
    current.includes(`-${recentlyMovedBand}`)
  ) {
    return current;
  }
  return [...current, recentlyMovedBand];
};

// Bare 'custom' is the range picker open with nothing picked yet: it filters
// nothing, so it is neither applied nor reported as dropped.
const pendingCustomDate = 'custom';

const isDateRange = value =>
  Array.isArray(value) &&
  value.length === 2 &&
  value.every(bound => typeof bound === 'string' && bound !== '');

// A preset the dropdown lists, or a picked [from, to] range, which it shows as Custom.
const isShowableOrgDate = value =>
  isDateRange(value) ||
  (value !== pendingCustomDate &&
    commonDateRanges.some(range => range.value === value));

/**
 * @typedef {Object} MappedAiFilters
 * @property {Record<string, any>} filters org-slice-shaped filters to merge in
 * @property {string[]} dropped AI keys that are not supported on the org profile
 * @property {Record<string, any>} droppedFilters those keys with their values,
 *   for review UI that shows what was asked for but will not be applied
 */

/**
 * Map a bag of SciSearch AI filters onto the organisation profile filter slice.
 * Allowed keys pass through (with the two renames applied and the named-author
 * booleans folded into a nested group); everything else is dropped and named.
 *
 * @param {Record<string, any> | null | undefined} aiFilters
 * @returns {MappedAiFilters}
 */
export function mapAiFiltersToOrgSlice(aiFilters) {
  const filters = {};
  const dropped = [];
  const droppedFilters = {};

  if (!aiFilters) {
    return { filters, dropped, droppedFilters };
  }

  const namedAuthor = {};
  let recentlyMoved = false;

  for (const [key, value] of Object.entries(aiFilters)) {
    if (key === recentlyMovedOrgKey) {
      // Translated, not dropped: it must not reach `dropped`, or the "filters
      // not applied" notice names it. Main search deletes the key on untick
      // and the schema is true-only, so anything but true means "absent".
      recentlyMoved = value === true;
      continue;
    }

    if (namedAuthorMemberKeys.includes(key)) {
      if (allowedAiFilterKeys.has(key)) {
        // The slice only ever holds `true` members (the UI deletes on
        // uncheck), so an explicit false from the AI means "absent" — storing
        // it would show a phantom filter token without affecting the query.
        if (value) {
          namedAuthor[key] = value;
        }
      } else {
        dropped.push(key);
        droppedFilters[key] = value;
      }
      continue;
    }

    const translated = aiKeyTranslations[key];
    if (translated) {
      filters[translated] = value;
      continue;
    }

    if (key === 'date' && !isShowableOrgDate(value)) {
      // The date itself is supported here, so name the unusable range, not the key.
      if (value != null && value !== pendingCustomDate) {
        dropped.push('date range');
        droppedFilters[key] = value;
      }
      continue;
    }

    if (allowedAiFilterKeys.has(key)) {
      filters[key] = value;
    } else {
      dropped.push(key);
      droppedFilters[key] = value;
    }
  }

  if (Object.keys(namedAuthor).length > 0) {
    filters.namedAuthor = namedAuthor;
  }

  // After the loop, so a timeAtCompany later in the bag is merged into rather
  // than overwriting the band.
  if (recentlyMoved) {
    filters.timeAtCompany = withRecentlyMovedBand(filters.timeAtCompany);
  }

  return { filters, dropped, droppedFilters };
}

/**
 * A filter value that cannot narrow a search: absent, blank, an empty
 * collection, or an object whose members are themselves all empty. `false` is
 * empty because the slice only ever stores enabled members (the UI deletes on
 * uncheck), so a false from the AI means "not asked for".
 *
 * @param {any} value
 * @returns {boolean}
 */
function isEmptyFilterValue(value) {
  if (
    value === undefined ||
    value === null ||
    value === false ||
    value === ''
  ) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object') {
    return Object.values(value).every(isEmptyFilterValue);
  }
  return false;
}

/**
 * @param {Record<string, any>} bag
 * @returns {boolean}
 */
const narrowsSomething = bag =>
  Object.values(bag ?? {}).some(value => !isEmptyFilterValue(value));

/**
 * @typedef {Object} OrgAiFilterSummary
 * @property {Record<string, any>} filters org-slice-shaped filters to merge in
 * @property {string[]} dropped AI keys the org profile does not support
 * @property {boolean} needsMainSearch the AI narrowed the search, but only with filters this profile drops
 */

/**
 * `mapAiFiltersToOrgSlice` plus the one question the org apply path has to ask
 * before it runs anything: can this profile serve the query?
 *
 * Everything the allow-set drops is a main-search filter by construction, so a
 * query that narrows only through dropped filters is one main search can serve.
 * No filters at all ("list all people") is the unfiltered profile search.
 *
 * @param {Record<string, any> | null | undefined} aiFilters
 * @returns {OrgAiFilterSummary}
 */
export function summariseOrgAiFilters(aiFilters) {
  const { filters, dropped } = mapAiFiltersToOrgSlice(aiFilters);

  return {
    filters,
    dropped,
    needsMainSearch: narrowsSomething(aiFilters) && !narrowsSomething(filters),
  };
}
